import Cookies from 'js-cookie'
import { ZodIssueCode, type z } from 'zod'
import { isOnServer } from '@shared/constants/util'
import { type RequestCookies } from '@shared/request_type'
import { logWarn } from '@/features/shared/utils/logger'
import { parseJson } from '@/features/shared/utils/parseJson'

type ValidatedCookie<TValue> = {
  name: string
  // client only read and returns the fallback on the server
  get: () => TValue
  // reads from a server provided cookies (SSR, middleware, RSC)
  from: (cookies: RequestCookies | undefined) => TValue
}

const warned = new Set<string>()

// path:code does NOT use zod's .message, .flatten() or .format()
// since some v3 messages embed the received value. (invalid_enum_value,
// invalid_literal), which would leak cookie contents into logs. paths and
// codes are safe and messages are not.
const summarizeIssues = (issues: z.ZodIssue[]) =>
  issues
    .slice(0, 5)
    .map(({ path, code }) => `${path.join('.') || '(root)'}:${code}`)
    .join(' ')

// a single invalid_type at the root means the cookie is not an object at all
// which is a scanner probe or hand edited junk, not a shape we ever wrote.
const isNotOurShape = (issues: z.ZodIssue[]) =>
  issues.length === 1 &&
  issues[0]?.path.length === 0 &&
  issues[0].code === ZodIssueCode.invalid_type

// this util is to ensure the cookie's value matches what we expect by validating a zod schema
export const defineCookie = <TSchema extends z.ZodTypeAny>({
  name,
  schema,
  fallback,
}: {
  name: string
  schema: TSchema
  fallback: z.infer<TSchema>
}): ValidatedCookie<z.infer<TSchema>> => {
  const parse = (raw: string | null | undefined): z.infer<TSchema> => {
    if (typeof raw !== 'string' || raw.trim() === '') return fallback

    const decoded = parseJson(raw)
    const candidate = typeof decoded === 'object' && decoded ? decoded : raw

    const result = schema.safeParse(candidate)
    if (result.success) return result.data

    const { issues } = result.error
    // garbage and drift are different problems, so log them under different keys
    const warnKey = isNotOurShape(issues) ? `${name}:unparseable` : name

    if (!warned.has(warnKey)) {
      warned.add(warnKey)
      // never log the value itself due to PII concerns
      logWarn('Invalid cookie value, falling back to default', {
        cookieName: name,
        issueCount: issues.length,
        issues: summarizeIssues(issues),
      })
    }
    return fallback
  }

  return {
    name,
    get: () => (isOnServer() ? fallback : parse(Cookies.get(name))),
    from: (cookies) => parse(cookies?.[name]),
  }
}
